import React, { useEffect, useState } from 'react';
import {
  Card, Button, Modal, Form, InputNumber, DatePicker, Upload, Space,
  message, Empty, Tag, Tooltip, Alert, Grid,
  theme,
} from 'antd';
import {
  UploadOutlined, DownloadOutlined, PlusOutlined, EyeOutlined, HistoryOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { fiscalDataService } from '../../services/api';
import ResponsiveTable from '../../components/Common/ResponsiveTable';

/**
 * Acuses de presentación de la empresa seleccionada: consulta por periodo y carga.
 *
 * LA FILA ES EL PERIODO, no el acuse. Un periodo puede tener varias versiones
 * (consecutivo 0 = declaración normal, 1..N = complementarias) y la fila muestra
 * la VIGENTE, la de consecutivo mayor. Esto no es cosmético: cuando la fila era
 * la versión, el botón de descarga —que baja la vigente del periodo— entregaba
 * el acuse de la complementaria aunque se pulsara en la fila de la normal.
 *
 * La carga se hace en nombre del cliente. El alcance lo valida el backend
 * DENTRO del service, no solo en la ruta.
 */
/**
 * `embebido`: dentro de la ficha, la sección plegable ya pone marco y título.
 */
const CaptureLinesPanel = ({ empresa, embebido = true }) => {
  const screens = Grid.useBreakpoint();
  const esMovil = !screens.md;
  const [modalAbierto, setModalAbierto] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [archivo, setArchivo] = useState(null);
  const [form] = Form.useForm();
  const { token } = theme.useToken();
  const [lineas, setLineas] = useState([]);
  const [loading, setLoading] = useState(true);
  // Periodo cuyo detalle de versiones está abierto.
  const [versionesDe, setVersionesDe] = useState(null);
  // Visor: { url, titulo }. El url es un blob que hay que revocar.
  const [visor, setVisor] = useState(null);
  const [abriendoVisor, setAbriendoVisor] = useState(false);

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresa?.id]);

  // Un blob URL sin revocar retiene el PDF en memoria mientras viva la pestaña.
  useEffect(() => () => {
    if (visor?.url) window.URL.revokeObjectURL(visor.url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visor?.url]);

  const cargar = async () => {
    if (!empresa?.id) return;
    setLoading(true);
    try {
      const data = await fiscalDataService.getCaptureLines(empresa.id);
      setLineas(data?.data?.items || data?.items || []);
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudieron cargar las líneas de captura');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Periodos, no acuses: agrupa las versiones que devuelve el backend y expone
   * la vigente de cada periodo.
   *
   * La vigente se calcula por el MÁXIMO consecutivo, sin confiar en el orden en
   * que llegó la lista: si alguien cambia el `orderBy` del backend, esto sigue
   * siendo correcto.
   */
  const periodos = Object.values(
    (lineas || []).reduce((acc, l) => {
      const consecutivo = Number(l.consecutivo ?? 0);
      const grupo = acc[l.period] || { period: l.period, versiones: [], vigente: null };

      grupo.versiones.push(l);
      if (!grupo.vigente || consecutivo > Number(grupo.vigente.consecutivo ?? 0)) {
        grupo.vigente = l;
      }

      acc[l.period] = grupo;
      return acc;
    }, {})
  )
    .map((g) => ({
      ...g.vigente,
      period: g.period,
      versiones: [...g.versiones].sort(
        (a, b) => Number(a.consecutivo ?? 0) - Number(b.consecutivo ?? 0)
      ),
      totalVersiones: g.versiones.length,
    }))
    .sort((a, b) => b.period.localeCompare(a.period));

  const nombreArchivo = (period, consecutivo) => {
    const sufijo = Number(consecutivo ?? 0) > 0 ? `-c${Number(consecutivo)}` : '';
    return `acuse-${empresa.tax_id}-${period}${sufijo}.pdf`;
  };

  /** `consecutivo` undefined = la vigente del periodo. */
  const descargar = async (period, consecutivo) => {
    try {
      const response = await fiscalDataService.downloadCaptureLine(empresa.id, period, consecutivo);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = nombreArchivo(period, consecutivo);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error('No se pudo descargar el PDF');
    }
  };

  /**
   * Ver sin descargar: el endpoint exige Authorization, así que no se puede
   * apuntar un iframe a la URL — hay que traer el blob y mostrarlo desde memoria.
   */
  const ver = async (period, consecutivo) => {
    setAbriendoVisor(true);
    try {
      const response = await fiscalDataService.downloadCaptureLine(empresa.id, period, consecutivo);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));

      // Revocar el anterior antes de reemplazarlo: ver varios PDF seguidos no
      // debe ir acumulando blobs.
      if (visor?.url) window.URL.revokeObjectURL(visor.url);

      const etiqueta = Number(consecutivo ?? 0) > 0
        ? `${period} · complementaria ${Number(consecutivo)}`
        : period;
      setVisor({ url, titulo: etiqueta });
    } catch (err) {
      message.error('No se pudo abrir el PDF');
    } finally {
      setAbriendoVisor(false);
    }
  };

  const cerrarVisor = () => {
    if (visor?.url) window.URL.revokeObjectURL(visor.url);
    setVisor(null);
  };

  const etiquetaVersion = (consecutivo) => (
    Number(consecutivo ?? 0) === 0
      ? <Tag>Normal</Tag>
      : <Tag color="purple">{`Complementaria ${Number(consecutivo)}`}</Tag>
  );

  /**
   * Versiones ya registradas del periodo elegido, para proponer la siguiente.
   * Se calcula sobre lo que hay en pantalla; si otro operador cargó en paralelo,
   * el backend rechaza con "Ya existe la complementaria N" y basta recargar.
   */
  const versionesDelPeriodo = (period) =>
    (lineas || []).filter((l) => l.period === period);

  const siguienteConsecutivo = (period) => {
    const previas = versionesDelPeriodo(period);
    if (previas.length === 0) return 0;
    return Math.max(...previas.map((l) => Number(l.consecutivo ?? 0))) + 1;
  };

  // Al cambiar el periodo hay que recalcular: si el usuario elige otro mes
  // después de ver la sugerencia, mandar el número viejo registraría una versión
  // equivocada.
  const alCambiarPeriodo = (valor) => {
    const period = valor ? valor.format('YYYY-MM') : null;
    form.setFieldsValue({ consecutivo: period ? siguienteConsecutivo(period) : 0 });
  };

  const subir = async (values) => {
    if (!archivo) {
      message.warning('Adjunta el PDF de la línea de captura');
      return;
    }

    setSubiendo(true);
    try {
      await fiscalDataService.uploadCaptureLine({
        userId: empresa.user_id,
        rfc: empresa.tax_id,
        amount: values.amount,
        period: values.period.format('YYYY-MM'),
        pdf: archivo,
        consecutivo: values.consecutivo ?? 0,
      });
      message.success(
        Number(values.consecutivo ?? 0) > 0
          ? `Complementaria ${Number(values.consecutivo)} cargada`
          : 'Línea de captura cargada'
      );
      setModalAbierto(false);
      setArchivo(null);
      form.resetFields();
      await cargar();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo cargar la línea de captura');
    } finally {
      setSubiendo(false);
    }
  };

  const columns = [
    {
      title: 'Periodo',
      dataIndex: 'period',
      key: 'period',
      render: (p, fila) => (
        <Space size={4} wrap>
          <Tag color="blue">{p}</Tag>
          {fila.totalVersiones > 1 && (
            <Tooltip title="Este periodo tiene complementarias. Se muestra la vigente.">
              <Tag color="purple">{`${fila.totalVersiones} versiones`}</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      // El importe es el de la VIGENTE: una complementaria sustituye lo
      // declarado, no se suma a la normal.
      title: 'Importe',
      dataIndex: 'amount',
      key: 'amount',
      render: (a) => `$${Number(a).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`,
    },
    {
      title: 'Cargada por',
      dataIndex: 'created_by',
      key: 'created_by',
      // Distinto de user_id: quién la subió, no de quién es.
      render: (v) => (v ? <Tag>{v}</Tag> : <Tag color="default">histórica</Tag>),
    },
    {
      title: '',
      key: 'actions',
      render: (_, fila) => (
        <Space wrap>
          <Button
            size="small"
            icon={<EyeOutlined />}
            loading={abriendoVisor}
            onClick={() => ver(fila.period)}
          >
            Ver
          </Button>
          <Button size="small" icon={<DownloadOutlined />} onClick={() => descargar(fila.period)}>
            PDF
          </Button>
          {fila.totalVersiones > 1 && (
            <Button
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => setVersionesDe(fila)}
            >
              {`Versiones (${fila.totalVersiones})`}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const columnasVersiones = [
    {
      title: 'Versión',
      dataIndex: 'consecutivo',
      key: 'consecutivo',
      render: (c) => etiquetaVersion(c),
    },
    {
      title: 'Importe',
      dataIndex: 'amount',
      key: 'amount',
      render: (a) => `$${Number(a).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`,
    },
    {
      title: 'Registrada',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (v) => (v ? String(v).slice(0, 16) : '—'),
    },
    {
      title: '',
      key: 'actions',
      render: (_, v) => (
        <Space wrap>
          <Button
            size="small"
            icon={<EyeOutlined />}
            loading={abriendoVisor}
            onClick={() => ver(v.period, Number(v.consecutivo ?? 0))}
          >
            Ver
          </Button>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => descargar(v.period, Number(v.consecutivo ?? 0))}
          >
            PDF
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={embebido ? null : 'Líneas de captura'}
      variant={embebido ? 'borderless' : 'outlined'}
      styles={{ body: { padding: embebido ? 0 : undefined } }}
      extra={
        esMovil ? null : (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalAbierto(true)}>
            Cargar línea
          </Button>
        )
      }
    >
      {/* Es la acción que más se repite en este panel. En móvil va al cuerpo y a
          ancho completo: en el `extra` queda apretada contra el borde. */}
      {esMovil && (
        <Button
          block
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={() => setModalAbierto(true)}
          style={{ marginBottom: 12 }}
        >
          Cargar línea
        </Button>
      )}
      <ResponsiveTable
        // La clave es el PERIODO, no el id: la fila representa el periodo y su
        // vigente puede cambiar de id al registrarse una complementaria.
        rowKey="period"
        loading={loading}
        dataSource={periodos}
        columns={columns}
        pagination={false}
        size="small"
        scroll={{ x: true }}
        locale={{ emptyText: <Empty description="Sin líneas de captura" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
      />

      {/* Detalle de versiones. En modal y no como fila expandible porque
          ResponsiveTable renderiza tarjetas en móvil e ignora `expandable`: las
          versiones quedarían invisibles en teléfono. */}
      <Modal
        title={`Versiones del acuse — ${versionesDe?.period || ''}`}
        open={!!versionesDe}
        onCancel={() => setVersionesDe(null)}
        footer={null}
        width={720}
      >
        <ResponsiveTable
          rowKey="id"
          dataSource={versionesDe?.versiones || []}
          columns={columnasVersiones}
          pagination={false}
          size="small"
          scroll={{ x: true }}
        />
        <span style={{ fontSize: 12, color: token.colorTextTertiary }}>
          La vigente es la última complementaria; su importe sustituye al de la
          declaración normal, no se suma. Ningún acuse se reemplaza: cada versión
          conserva el suyo.
        </span>
      </Modal>


      {/* Visor: el PDF vive en un blob en memoria, no en una URL con token. */}
      <Modal
        title={`Acuse — ${visor?.titulo || ''}`}
        open={!!visor}
        onCancel={cerrarVisor}
        footer={
          <Space>
            {visor?.url && (
              <Button
                onClick={() => window.open(visor.url, '_blank', 'noopener')}
              >
                Abrir en pestaña nueva
              </Button>
            )}
            <Button onClick={cerrarVisor}>Cerrar</Button>
          </Space>
        }
        width={esMovil ? '100%' : 900}
        style={esMovil ? { top: 8, maxWidth: '100vw', margin: 0, padding: 0 } : undefined}
        styles={{ body: { padding: 0 } }}
      >
        {visor?.url && (
          <iframe
            src={visor.url}
            title="Acuse de presentación"
            style={{ width: '100%', height: '70vh', border: 0 }}
          />
        )}
        <div style={{ padding: '8px 16px', fontSize: 12, color: token.colorTextTertiary }}>
          Si el PDF no se muestra, usa “Abrir en pestaña nueva”: algunos
          navegadores móviles no lo incrustan.
        </div>
      </Modal>

      <Modal
        title={`Cargar línea de captura — ${empresa?.tax_id}`}
        open={modalAbierto}
        onCancel={() => setModalAbierto(false)}
        onOk={() => form.submit()}
        confirmLoading={subiendo}
        okText="Cargar"
        cancelText="Cancelar"
        // Es la acción más frecuente del panel y se usa desde el celular: a
        // pantalla completa, y con los botones grandes para el pulgar.
        width={esMovil ? '100%' : 520}
        style={esMovil ? { top: 0, maxWidth: '100vw', margin: 0, paddingBottom: 0 } : undefined}
        okButtonProps={{ size: esMovil ? 'large' : 'middle' }}
        cancelButtonProps={{ size: esMovil ? 'large' : 'middle' }}
      >
        <Form form={form} layout="vertical" onFinish={subir} initialValues={{ consecutivo: 0 }}>
          <Form.Item name="period" label="Periodo" rules={[{ required: true, message: 'Requerido' }]}>
            <DatePicker
              picker="month"
              style={{ width: '100%' }}
              disabledDate={(d) => d && d > moment()}
              onChange={alCambiarPeriodo}
            />
          </Form.Item>

          {/* Versión del acuse. El campo aparece con contexto solo cuando el
              periodo elegido ya tiene acuses: en un periodo nuevo no hay nada que
              decidir y se registra como la declaración normal. */}
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => {
              const period = getFieldValue('period')?.format('YYYY-MM');
              const previas = period ? versionesDelPeriodo(period) : [];
              const consecutivo = Number(getFieldValue('consecutivo') ?? 0);

              if (previas.length === 0) {
                // Sin versiones previas: mantener el valor en el formulario sin
                // pedirle al usuario que entienda el concepto.
                return <Form.Item name="consecutivo" hidden><InputNumber /></Form.Item>;
              }

              return (
                <>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={`Este periodo ya tiene ${previas.length} ${previas.length === 1 ? 'acuse' : 'acuses'}`}
                    description={
                      consecutivo > 0
                        ? `Se registrará como complementaria ${consecutivo}. El acuse anterior se conserva: nada se reemplaza.`
                        : 'Con versión 0 se intentará registrar la declaración normal, que ya existe y será rechazada.'
                    }
                  />
                  <Form.Item
                    name="consecutivo"
                    label="Versión"
                    rules={[{ required: true, message: 'Requerido' }]}
                    extra="0 es la declaración normal; 1 en adelante son complementarias. Se propone la siguiente, pero puedes ajustarla si la normal se presentó fuera de este sistema."
                  >
                    <InputNumber min={0} max={99} step={1} precision={0} style={{ width: '100%' }} />
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>
          {/* min={0} y NO 0.01: una declaración en ceros o a favor se presenta
              igual y su acuse existe. Con el mínimo en 0.01, rc-input-number
              reajustaba el 0 al salir del campo y registraba un pago de un
              centavo que el contribuyente no debe. */}
          <Form.Item
            name="amount"
            label="Importe a pagar"
            rules={[{ required: true, message: 'Requerido' }]}
            extra="Captura 0 si la declaración quedó en ceros o a favor: se registra igual, con su acuse."
          >
            <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="PDF" required>
            <Upload
              accept="application/pdf"
              maxCount={1}
              beforeUpload={(file) => { setArchivo(file); return false; }}
              onRemove={() => setArchivo(null)}
              fileList={archivo ? [archivo] : []}
            >
              <Button icon={<UploadOutlined />} block={esMovil} size={esMovil ? 'large' : 'middle'}>
                Seleccionar PDF
              </Button>
            </Upload>
          </Form.Item>
          <Space direction="vertical" size={0}>
            <span style={{ fontSize: 12, color: token.colorTextTertiary }}>
              La línea quedará registrada a nombre del contribuyente, contigo como autor de la carga.
            </span>
          </Space>
        </Form>
      </Modal>
    </Card>
  );
};

export default CaptureLinesPanel;
